/**
 * The name -> value mapping editor — ADR-128's structural-editing gap, for the three sites
 * that hold one: `set`, `python.inputs` and `run.inputs`.
 *
 * WHY THESE THREE COULD NOT BE WIRED BEFORE
 * ----------------------------------------
 * `validate_data_refs.SLOT_WANTS` has offered references for all three since the slot work,
 * and `test_dsl_contract_parity` asserted the gap explicitly so it could not be forgotten:
 * offers existed, and no editor existed to open a picker from. The reason was not a missing
 * `slot=` attribute. `DataRefFieldAffordance` wraps ONE field, and these sites hold a
 * collection of independently-referenceable values — so each row needs its own picker, and
 * rows have to be addable and removable. That is a different control, which is this file.
 *
 * WHAT IT REFUSES, AND WHY IT SAYS SO INSTEAD OF FIXING IT QUIETLY
 * --------------------------------------------------------------
 * Two states are unwritable and both would LOSE something silently:
 *
 *   - a DUPLICATE name, because a YAML mapping cannot hold one twice — one row would be
 *     discarded with nothing to say which;
 *   - an EMPTY `set`, because the canonical validator rejects it, so writing it would
 *     produce a CI failure from a click.
 *
 * In both cases the draft keeps its last valid mapping and the refusal is shown. Silently
 * writing the "best guess" is what makes an editor untrustworthy: the file would stop
 * matching what is on screen, and nothing would say when that started.
 *
 * The rules themselves live in `workflowDraft.mappingRefusal`, next to the mutator that
 * enforces them, so the display and the write cannot disagree about what is legal.
 *
 * WHY LOCAL ROW STATE
 * ------------------
 * A mapping key is its own identity in YAML, so a row being RENAMED has no stable identity
 * in the document — mid-rename it is `{'': value}` or a duplicate. Rows therefore carry a
 * local id, seeded from the task and re-seeded when the selected task changes. Every edit is
 * still pushed straight into the draft, so nothing is held back from `draftYAML` and a mode
 * switch stays lossless (ADR-079 D2).
 */

import { useEffect, useMemo, useState } from 'react'

import type { DataReferenceMatch, DataReferenceMatchLocation } from '../api/client'
import { DataRefFieldAffordance } from './DataRefFieldAffordance'
import {
  mappingRefusal, type DataReferenceSelection, type MappingEntry, type MappingSite,
} from './workflowDraft'

/** What each site is FOR, in the operator's terms. The DSL name alone does not say whether
 *  a value is an expression or a template, and the difference is visible in what the picker
 *  inserts, so it is worth one line here. */
const SITE_COPY: Record<MappingSite, { title: string; keyLabel: string; hint: string }> = {
  'set': {
    title: 'Variables this task binds',
    keyLabel: 'variable',
    hint: 'Each value is an EXPRESSION — an inserted reference reads `.data.alias.field`, ' +
      'with no braces. A set must bind at least one variable.',
  },
  'python.inputs': {
    title: 'Inputs passed to the code',
    keyLabel: 'input',
    hint: 'The code reads these from its `inputs` dict — the only way values enter it. ' +
      'Values are TEMPLATES, so an inserted reference reads `{{data.alias.field}}`.',
  },
  'run.inputs': {
    title: 'Inputs passed to the sub-workflow',
    keyLabel: 'input',
    hint: 'These become the sub-workflow’s `{{inputs.*}}`. Values are TEMPLATES, so an ' +
      'inserted reference reads `{{data.alias.field}}`.',
  },
}

interface Row extends MappingEntry {
  /** Local identity, because a mapping key is its own identity in YAML and a row being
   *  renamed therefore has none. Never written to the document. */
  id: number
}

/** The rows plus the next unused id. One state value so that adding a row allocates its id
 *  in the same update that appends it — two pieces of state could disagree after a batch. */
interface EditorState {
  rows: Row[]
  next: number
}

function seedState(from: MappingEntry[]): EditorState {
  return { rows: from.map((e, i) => ({ ...e, id: i })), next: from.length }
}

export interface TaskMappingEditorProps {
  /** ADR-153 D3 — the draft, so each row can also offer the workflow-local
   *  values a `set` bound earlier. Optional: a host without one simply shows no
   *  workflow-value button. */
  draft?: string
  site: MappingSite
  /** The task's current mapping, read from the draft. */
  entries: MappingEntry[]
  /** Re-seeds the rows when the selection moves to another task. */
  taskName: string
  /** Called with the FULL new mapping on every change the editor accepts. */
  onChange: (entries: MappingEntry[]) => void
  /** ADR-127 D15 — establish the `data:` pin an inserted reference needs, or refuse. */
  onEnsurePin?: (pin: { alias: string; set: string }) => string | null | void
  /** ADR-179 — what the matcher found at one row's location, or null. The editor computes
   *  no matches of its own: a row is addressed by `{task, slot, key}` and the page answers.
   *  Absent, no hint renders, which is every host that does not run the matcher. */
  lookupMatch?: (location: DataReferenceMatchLocation) => DataReferenceMatch | null
  /** ADR-179 — report a picked reference with its pin, for the page to apply as one draft
   *  mutation. Supersedes `onEnsurePin` + `onChange` for the rows that have it. */
  onApplyReferences?: (selections: DataReferenceSelection[]) => void
}

export function TaskMappingEditor({
  site, entries, taskName, onChange, onEnsurePin, draft, lookupMatch, onApplyReferences,
}: TaskMappingEditorProps) {
  // The id counter lives in STATE beside the rows, not in a ref: every allocation happens in
  // an event handler or an effect, so nothing reads a mutable value during render (which is
  // both the react-hooks/refs rule and a real hazard — a render React discards would still
  // have consumed ids).
  const [state, setState] = useState<EditorState>(() => seedState(entries))
  const rows = state.rows
  const [applyRefusal, setApplyRefusal] = useState<string | null>(null)

  // Re-seed on a change of TASK, not of entries: re-seeding on every entries change would
  // fight the operator's own typing, since each keystroke pushes a new mapping up and gets
  // it back. The document remains the source of truth across a selection change.
  useEffect(() => {
    setState(seedState(entries))
    // The refusal names a row of the task being left, so on any other task it is a false
    // statement about a mapping the operator has not touched.
    setApplyRefusal(null)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- see above: taskName only
  }, [taskName])

  // ADR-179 — an apply is addressed in the PARSED draft (`{task, slot, key}`), which is what
  // `entries` holds. The local rows are not the same thing whenever `commit` has withheld a
  // write, so both the affordance's stale-DOM guard and the apply itself are judged against
  // this map rather than against the row the operator is looking at.
  const draftValues = useMemo(() => new Map(entries.map(e => [e.key, e.value])), [entries])

  const refusal = mappingRefusal(site, rows)
  const copy = SITE_COPY[site]

  /** Push a row set up, unless it is one of the unwritable states. The refusal is rendered
   *  either way, so an operator is never left wondering why the file did not change. */
  const commit = (next: Row[]) => {
    setState(s => ({ ...s, rows: next }))
    setApplyRefusal(null)
    if (!mappingRefusal(site, next)) onChange(next.map(({ key, value }) => ({ key, value })))
  }

  /** Forward a picked reference only for a row the draft actually holds, and only while the
   *  whole row set is writable. Both states below would otherwise write to the WRONG text:
   *  the document still holds the mapping from before the refused edit, so a caret offset
   *  taken from the row on screen addresses a string that is not there — and a row renamed
   *  onto another row's name addresses that other row. */
  const applyFor = onApplyReferences === undefined ? undefined : (row: Row) =>
    (selections: DataReferenceSelection[]) => {
      const key = row.key.trim()
      if (refusal) {
        setApplyRefusal(
          'The mapping above cannot be saved yet, so this row is not in the workflow as ' +
          'shown. Resolve the problem below, then insert the reference.')
        return
      }
      if (!draftValues.has(key)) {
        setApplyRefusal(
          `No \`${key}\` ${copy.keyLabel} is saved in the workflow yet, so there is ` +
          'nothing to insert into. Save this row first.')
        return
      }
      setApplyRefusal(null)
      onApplyReferences(selections)
    }

  return (
    <div className="space-y-1" data-testid={`mapping-${site}`}>
      <p className="text-xs text-gray-500">{copy.title}</p>
      {rows.map((row, i) => {
        // The row's address in the parsed draft. A mapping key is the address, so a row
        // mid-rename simply matches nothing — which is the honest answer. Trimmed, because
        // `updateTaskMapping` writes `key.trim()`: the document never holds the surrounding
        // whitespace, so addressing the row by the untrimmed text misses a row that is saved.
        const key = row.key.trim()
        const location = { task: taskName, slot: site, key }
        const match = lookupMatch?.(location) ?? null
        return (
        <div key={row.id} className="flex items-start gap-1" data-testid={`mapping-row-${i}`}>
          <input
            value={row.key}
            onChange={e => commit(rows.map(r => r.id === row.id ? { ...r, key: e.target.value } : r))}
            placeholder={copy.keyLabel}
            aria-label={`${copy.keyLabel} name`}
            data-testid={`mapping-key-${i}`}
            className="w-1/3 text-xs font-mono border border-gray-200 rounded px-2 py-1"
          />
          <div className="flex-1">
            <DataRefFieldAffordance
              slot={site}
              // Scoped to the TASK, and kept AFTER `slot={site}` for the same reason
              // `varsContext` is — the ADR-128 D3 parity guard matches
              // `<DataRefFieldAffordance\s+slot={site}`. `seedState` re-issues row ids as
              // 0..n-1 for the new task, so without this React reuses the instance and its
              // per-field state: a refusal or an open hint panel raised on the previous
              // task would render under the new task's row of the same index.
              key={`${taskName}:${row.id}`}
              // ADR-153 D3 — the second source. Kept AFTER `slot={site}`: the
              // ADR-128 D3 parity guard matches `<DataRefFieldAffordance\s+slot={site}`
              // to prove the mapping sites still reach the picker, and that guard
              // is worth more than the attribute order.
              varsContext={draft === undefined ? undefined : { draft, task: taskName }}
              instance={String(i)}
              location={location}
              matches={match ? [match] : undefined}
              draftValue={draftValues.get(key)}
              onApplyReferences={applyFor?.(row)}
              onEnsurePin={onEnsurePin}
              onChange={value =>
                commit(rows.map(r => r.id === row.id ? { ...r, value } : r))}
            >
              {ref => (
                <input
                  ref={ref as React.RefObject<HTMLInputElement>}
                  value={row.value}
                  onChange={e =>
                    commit(rows.map(r => r.id === row.id ? { ...r, value: e.target.value } : r))}
                  placeholder="value"
                  aria-label={`${copy.keyLabel} value`}
                  data-testid={`mapping-value-${i}`}
                  className="w-full text-xs font-mono border border-gray-200 rounded px-2 py-1"
                />
              )}
            </DataRefFieldAffordance>
          </div>
          <button
            type="button"
            // A `set` with one row left has nothing to remove TO: the validator rejects an
            // empty mapping, so the button is disabled rather than offering an edit that
            // cannot be saved. Deleting the whole task is the real gesture, and it lives in
            // the palette.
            disabled={site === 'set' && rows.length === 1}
            title={site === 'set' && rows.length === 1
              ? 'A set must bind at least one variable — remove the task itself instead.'
              : `Remove this ${copy.keyLabel}`}
            onClick={() => commit(rows.filter(r => r.id !== row.id))}
            data-testid={`mapping-remove-${i}`}
            className="text-xs text-gray-400 hover:text-red-700 disabled:text-gray-200
                       disabled:hover:text-gray-200 px-1 py-1"
          >
            ✕
          </button>
        </div>
        )
      })}
      <button
        type="button"
        // An unnamed row is not a binding, so adding one does NOT commit: the document is
        // unchanged until the operator names it.
        onClick={() => setState(s => ({
          rows: [...s.rows, { key: '', value: '', id: s.next }], next: s.next + 1,
        }))}
        data-testid={`mapping-add-${site}`}
        className="text-xs text-indigo-700 hover:underline"
      >
        ＋ add {copy.keyLabel}
      </button>
      {refusal && (
        <p className="text-xs text-amber-800" data-testid={`mapping-refusal-${site}`}>
          {refusal}
        </p>
      )}
      {applyRefusal && (
        <p className="text-xs text-amber-800" data-testid={`mapping-apply-refusal-${site}`}>
          {applyRefusal}
        </p>
      )}
      <p className="text-[10px] text-gray-400">{copy.hint}</p>
    </div>
  )
}
